import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { DiscountFields, DiscountMode } from "@/features/invoices/use-invoice-editor"
import type { DocumentTotals } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { parsePercentToBasisPoints } from "@/lib/units"
import { formatPercentBp } from "@steelbill/types"

/**
 * §15(3)(a) — the discount boxes, per line and for the whole document.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * PERCENTAGE IS AN INPUT; PAISE IS THE RECORD. NOTHING HERE COMPUTES A DISCOUNT.
 *
 * He types `10`; the SERVER resolves it to paise once, at save, against that
 * line's gross taxable value (or, for the whole document, the subtotal after the
 * per-line discounts) through `divRoundHalfUp`, and stores the resulting AMOUNT.
 * §15(3)(a) excludes a discount from the value of supply only when the discount
 * is recorded in the invoice, and the money is what is recorded; the percentage
 * is kept beside it as provenance and is never re-evaluated on read.
 *
 * So this file contains NO ARITHMETIC. Every rupee figure below is read out of
 * `POST /…/preview`, which runs the same pipeline the save runs. A "quick"
 * `gross × bp / 10000` in a component would be a second tax engine — CLAUDE.md's
 * flat prohibition — and on a `BY_WEIGHT` line it would round differently from
 * the sheet the customer receives.
 *
 * AND THE TWO FORMS ARE MUTUALLY EXCLUSIVE ON THE WIRE. `discountBody` sends
 * `discountPaise` XOR `discountPercentBp`; both together is a 400 by design,
 * because two answers to "what is the discount" means the server picking one
 * silently, and the one it picks is the one the owner was not looking at.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * The percentage to show in a `(10%)` bracket beside a discount — or `null`,
 * which is the answer far more often than it is not.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SCREEN MUST NOT PROMISE A BRACKET THE SHEET WILL NOT PRINT.
 *
 * The PDF prints `LESS DISCOUNT (10%)` only when the percentage still reproduces
 * the figure beside it EXACTLY — `lessDiscountLabel` and `lineDiscountNote` in
 * services/pdf/blocks.ts both re-derive it and fall back to the plain label when
 * it does not. That check exists because the bracket is there to be CHECKED: a
 * quotation discounted 10% off ₹6,500 (₹650.00) whose line is later re-rated to
 * ₹8,500 keeps the ₹650.00 that was agreed, and a bracket claiming 10% of 8,500
 * is one the customer's AP team can disprove with the two numbers in front of
 * them. One that fails its own check is worse than none.
 *
 * THIS SIDE CANNOT RUN THAT CHECK, AND MUST NOT TRY. Reproducing it needs
 * `resolveDiscountPercent` — `mulExact` then `divRoundHalfUp` over the line's
 * gross — and services/documents.ts is explicit that the function stays out of
 * `packages/tax` on purpose, so that nothing but the save can ever turn a
 * percentage into money. Copying it here would be exactly the duplicate both
 * that comment and CLAUDE.md forbid.
 *
 * SO THE BRACKET IS SHOWN ONLY IN THE ONE CASE IT IS PROVABLE WITHOUT
 * ARITHMETIC: the owner is typing a percentage RIGHT NOW, and the response on
 * screen is the server's answer TO THAT PERCENTAGE. Then the money beside it was
 * resolved from this very figure moments ago, so the sheet's check passes by
 * construction. In every other case — a document loaded back from the server,
 * whose stored percentage is provenance the client cannot verify — no bracket is
 * shown. The figure is still right; only the label is quieter than the paper
 * might be, and a missing bracket is a smaller lie than a wrong one.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function bracketPercentBp(args: {
  /** Which form the box is in. `"AMOUNT"` can never carry a bracket. */
  mode: DiscountMode
  /** The percentage as typed, `"10"` / `"7.5"`. */
  typed: string
  /** What the SERVER echoed for this discount — `null` when it took an amount. */
  serverPercentBp: number | null
  /**
   * The sheet drops the bracket here for a reason of its own, whatever the
   * percentage says. Used for the whole-document row, which prints no bracket
   * when per-line discounts also exist: its base is then the subtotal AFTER
   * those, which the printed band deliberately does not show, so a bracket would
   * be a percentage of nothing visible (`lessDiscountLabel`).
   */
  suppressed?: boolean
}): number | null {
  if (args.mode !== "PERCENT") return null
  if (args.suppressed === true) return null
  if (args.serverPercentBp === null) return null

  const typedBp = parsePercentToBasisPoints(args.typed.trim())
  // Mid-debounce the response on screen still answers the PREVIOUS keystroke.
  // Showing its bracket against what is in the box now would caption a figure
  // with a percentage that did not produce it — briefly, and wrongly.
  return typedBp === args.serverPercentBp ? typedBp : null
}

/**
 * Amount ⇄ percent, for one discount.
 *
 * TWO BUTTONS RATHER THAN A SELECT, because this is a two-state choice sitting
 * beside a number box on a phone, and a dropdown costs a tap and an overlay to
 * answer a question with two answers. `aria-pressed` is what makes the current
 * one announce as chosen; the visible glyphs are `₹` and `%`, and the accessible
 * names are full sentences supplied by the caller, so a line's toggle and the
 * document's are never the same control to a screen reader or to a test.
 *
 * SWITCHING KEEPS BOTH BOXES' TEXT. Clearing the one being left would throw away
 * a figure the owner typed on a mis-tap, and nothing stale can leak: the mode is
 * what `discountBody` reads first, and it returns one key or none.
 */
function DiscountModeToggle({
  mode,
  amountLabel,
  percentLabel,
  onChange,
}: {
  mode: DiscountMode
  amountLabel: string
  percentLabel: string
  onChange: (mode: DiscountMode) => void
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        size="sm"
        variant={mode === "AMOUNT" ? "secondary" : "ghost"}
        aria-pressed={mode === "AMOUNT"}
        aria-label={amountLabel}
        onClick={() => {
          onChange("AMOUNT")
        }}
      >
        ₹
      </Button>
      <Button
        type="button"
        size="sm"
        variant={mode === "PERCENT" ? "secondary" : "ghost"}
        aria-pressed={mode === "PERCENT"}
        aria-label={percentLabel}
        onClick={() => {
          onChange("PERCENT")
        }}
      >
        %
      </Button>
    </div>
  )
}

/**
 * The discount on ONE LINE (§15(3)(a)), taken before tax.
 *
 * WHAT THE PERCENTAGE COMES TO IS SHOWN, AND IT IS THE SERVER'S FIGURE. That is
 * the whole value of the percent box: he types 10 and needs to see ₹650.00
 * before he hands the paper over. It is read from the preview line, never
 * multiplied here.
 *
 * ABSENT ENTIRELY on a challan and on a credit note — the caller decides, via
 * `LineCard`'s required `discountable` prop. Not disabled and not hidden with a
 * value still in state: those schemas STRIP `discountPaise` as an unknown key,
 * so an unreachable field that still posts changes nothing and says nothing.
 */
export function LineDiscountField({
  line,
  lineKey,
  preview,
  onChange,
}: {
  line: DiscountFields
  /** Only to make the label's `htmlFor` unique across the lines on screen. */
  lineKey: string
  /**
   * THE SERVER'S ANSWER FOR THIS LINE. `discountPaise` is what the percentage
   * came to; `discountPercentBp` is the percentage it says it used, and the two
   * agreeing with the box is what earns the bracket.
   */
  preview: { discountPaise?: number; discountPercentBp?: number | null } | undefined
  onChange: (changes: Partial<DiscountFields>) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const percent = line.discountMode === "PERCENT"

  const appliedPaise = preview?.discountPaise ?? 0
  const bracketBp = bracketPercentBp({
    mode: line.discountMode,
    typed: line.discountPercent,
    serverPercentBp: preview?.discountPercentBp ?? null,
  })

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={`line-discount-${lineKey}`}>
          {percent ? t("line.discountPercent") : t("line.discount")}
        </Label>
        <DiscountModeToggle
          mode={line.discountMode}
          amountLabel={t("line.discountInRupees")}
          percentLabel={t("line.discountInPercent")}
          onChange={(discountMode) => {
            onChange({ discountMode })
          }}
        />
      </div>

      <Input
        id={`line-discount-${lineKey}`}
        value={percent ? line.discountPercent : line.discount}
        inputMode="decimal"
        className="tabular-nums"
        placeholder={percent ? "0" : "0.00"}
        onChange={(event) => {
          onChange(percent ? { discountPercent: event.target.value } : { discount: event.target.value })
        }}
      />

      {/*
        * WHAT IT CAME TO, IN RUPEES — only in percent mode, and only once the
        * server has answered. In amount mode the box already IS the figure, and
        * repeating it under itself would read as a second reduction.
        */}
      {percent && appliedPaise > 0 ? (
        <p className="text-muted-foreground text-xs tabular-nums">
          {bracketBp === null
            ? t("line.discountApplied", { amount: formatRupees(appliedPaise) })
            : t("line.discountAppliedPercent", {
                percent: formatPercentBp(bracketBp),
                amount: formatRupees(appliedPaise),
              })}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The discount on the document AS A WHOLE (§15(3)(a)).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS NOT SUBTRACTED FROM THE TOTAL, AND THAT IS WHY THIS IS A FIELD AND NOT
 * A SUM DONE ON THIS SCREEN.
 *
 * The lines carry different GST rates (18% fabrication, 5%/12%/18% works
 * contract — §8.1), so the server apportions the figure across them in
 * proportion to their post-line-discount taxable values and recomputes each
 * line's tax at its own rate. Taking ₹1,000 off the grand total instead would
 * print a wrong CGST and a wrong SGST on a legal document — which is exactly the
 * mistake this whole field exists to stop the owner making with the RATE column.
 *
 * SO EVERY FIGURE BELOW THE BOX IS THE SERVER'S, read out of the preview
 * response. Nothing here divides anything; `divRoundHalfUp` and the
 * largest-remainder allocation live in `@steelbill/tax` and run once, on the
 * server, for both the preview and the save (§6, §2.1).
 *
 * A PERCENTAGE HERE IS OF THE SUBTOTAL AFTER THE PER-LINE DISCOUNTS, which is
 * the same base the amount form is applied to — so "10% off the job" and "the
 * rupees 10% comes to" store, tax and print identical figures. The help text
 * says so, because it is the question the owner will ask.
 *
 * SHARED WITH THE QUOTATION EDITOR, exactly as `LineCard` and `TotalsBar` are —
 * both document kinds send the same keys to the same schema, and a second copy
 * would drift the first time either was touched.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function DocumentDiscountField({
  value,
  totals,
  onChange,
}: {
  value: DiscountFields
  /** The server's figures, or `undefined` while the preview is not computable. */
  totals: DocumentTotals | undefined
  onChange: (changes: Partial<DiscountFields>) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const percent = value.discountMode === "PERCENT"
  const applied = totals !== undefined && totals.totalDiscountPaise > 0

  const bracketBp = bracketPercentBp({
    mode: value.discountMode,
    typed: value.discountPercent,
    serverPercentBp: totals?.documentDiscountPercentBp ?? null,
    /*
     * The printed band drops the bracket whenever per-line discounts also exist
     * (`lessDiscountLabel`): the row above it is the GROSS, the percentage was
     * taken of the subtotal after the line discounts, and that base is nowhere on
     * the page. The screen keeps the same silence rather than promising a bracket
     * the sheet will not print.
     */
    suppressed: (totals?.lineDiscountTotalPaise ?? 0) !== 0,
  })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5 sm:max-w-xs">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="document-discount">
            {percent ? t("doc.discountPercent") : t("doc.discount")}
          </Label>
          <DiscountModeToggle
            mode={value.discountMode}
            amountLabel={t("doc.discountInRupees")}
            percentLabel={t("doc.discountInPercent")}
            onChange={(discountMode) => {
              onChange({ discountMode })
            }}
          />
        </div>
        <Input
          id="document-discount"
          value={percent ? value.discountPercent : value.discount}
          inputMode="decimal"
          className="tabular-nums"
          placeholder={percent ? "0" : "0.00"}
          onChange={(event) => {
            onChange(
              percent ? { discountPercent: event.target.value } : { discount: event.target.value },
            )
          }}
        />
        <p className="text-muted-foreground text-xs">
          {percent ? t("doc.discountPercentHelp") : t("doc.discountHelp")}
        </p>
      </div>

      {/*
        * THE DERIVATION, AS THE PRINTED SHEET STATES IT (§9.2b). The owner is
        * about to hand this document to a customer; the screen he decided on it
        * from should read the same way the paper does — including the bracket,
        * and including its absence.
        */}
      {applied ? (
        <dl className="grid max-w-xs grid-cols-2 gap-x-4 gap-y-1 text-sm tabular-nums">
          <dt className="text-muted-foreground">{t("doc.beforeDiscount")}</dt>
          <dd className="text-right">{formatRupees(totals.grossSubtotalPaise)}</dd>
          <dt className="text-muted-foreground">
            {bracketBp === null
              ? t("doc.lessDiscount")
              : t("doc.lessDiscountPercent", { percent: formatPercentBp(bracketBp) })}
          </dt>
          <dd className="text-right">{formatRupees(totals.totalDiscountPaise)}</dd>
          <dt className="text-muted-foreground">{t("lbl.subtotal")}</dt>
          <dd className="text-right font-medium">{formatRupees(totals.subtotalPaise)}</dd>
        </dl>
      ) : null}
    </div>
  )
}
